import React from 'react';
import PropTypes from 'prop-types';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { analyticsChartTheme, formatCompactMetric } from 'util/chartUtils';
import IntlMessages from '../../../../util/IntlMessages';
import DashboardPanel from './DashboardPanel';
import styles from './DashboardChart.module.scss';

const COLORS = [
  analyticsChartTheme.searches,
  analyticsChartTheme.clickRate,
  analyticsChartTheme.clickEvents,
  analyticsChartTheme.warning,
  analyticsChartTheme.auxiliary,
];

export const buildSearchTrendRows = (demand) => (demand?.xAxis || []).map((label, index) => (
  (demand.curves || []).reduce((row, curve) => ({
    ...row,
    [curve.name]: curve.data[index] || 0,
  }), { label })
));

const SearchTrends = ({ demand }) => {
  const empty = !demand || Boolean(demand.error) || !demand.xAxis?.length || !demand.curves?.length;
  const data = empty ? [] : buildSearchTrendRows(demand);

  return (
    <DashboardPanel
      title={<IntlMessages id="dashboard.searchTrends.header" />}
      subtitle={<IntlMessages id="dashboard.searchTrends.subheader" />}
      note={<IntlMessages id="dashboard.searchTrends.subsubheader" />}
      tone="clickRate"
      empty={empty}
      emptyVariant="line"
    >
      <div className={styles.chartFrame}>
        <ResponsiveContainer width="100%" height={330}>
          <LineChart data={data} margin={{ top: 12, right: 18, left: 0, bottom: 12 }}>
            <CartesianGrid stroke={analyticsChartTheme.grid} vertical={false} strokeDasharray="2 7" />
            <XAxis dataKey="label" axisLine={false} tickLine={false} minTickGap={26} tick={{ fill: analyticsChartTheme.muted, fontSize: 10 }} />
            <YAxis axisLine={false} tickLine={false} width={48} tickFormatter={formatCompactMetric} tick={{ fill: analyticsChartTheme.muted, fontSize: 10 }} />
            <Tooltip />
            <Legend />
            {demand?.curves?.map((curve, index) => (
              <Line key={curve.name} dataKey={curve.name} stroke={COLORS[index % COLORS.length]} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </DashboardPanel>
  );
};

SearchTrends.propTypes = {
  demand: PropTypes.shape({
    curves: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      data: PropTypes.arrayOf(PropTypes.number),
    })),
    xAxis: PropTypes.arrayOf(PropTypes.string),
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  }),
};

SearchTrends.defaultProps = { demand: null };

export default SearchTrends;
